/**
 * Mobile-host detection for the m.idarah.io mobile-first experience.
 *
 * The same SPA build serves both the desktop host (idarah.io) and the
 * mobile host (m.idarah.io). On the mobile host the app renders a
 * square-apps landing grid and a sidebar-free shell.
 *
 * For local development the mobile experience can be forced by visiting
 * any page with `?mobile-host=1` (persists in localStorage) and reverted
 * with `?mobile-host=0`.
 */

const FORCE_KEY = "forceMobileHost";

// Apply the dev override query param once at module load.
if (typeof window !== "undefined") {
  const params = new URLSearchParams(window.location.search);
  const override = params.get("mobile-host");
  if (override === "1") {
    window.localStorage.setItem(FORCE_KEY, "1");
  } else if (override === "0") {
    window.localStorage.removeItem(FORCE_KEY);
  }
}

export const isMobileHost = (): boolean => {
  if (typeof window === "undefined") return false;
  if (window.localStorage.getItem(FORCE_KEY) === "1") return true;
  return window.location.hostname.startsWith("m.");
};

/**
 * Phone/tablet guests on the desktop hosts are redirected to the mobile host
 * (m.idarah.io) so field users land on the mobile shell and install the right
 * PWA origin. Sessions are per-origin, so the redirect runs before render in
 * index.tsx. Returns the full target URL, or null when no redirect applies.
 * Only the two desktop apex hosts redirect: localhost, staging, the mobile
 * host itself, and other custom hosts keep their own experience.
 */
const DESKTOP_REDIRECT_HOSTS = ["idarah.io", "www.idarah.io"];

export function mobileRedirectTarget(
  hostname: string,
  pathname: string,
  search: string,
): string | null {
  if (!DESKTOP_REDIRECT_HOSTS.includes(hostname)) return null;
  return `https://m.idarah.io${pathname}${search}`;
}
